// @flow
import React from 'react';
import { convertToRaw } from 'draft-js';
import type { Editor } from 'draft-js';
import { EntryEditor } from '@finect/tabular-draft-editor';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { Check } from '@finect/tabular-icons/check';
import {
  PendingModerationAlert,
  MentionedProducts,
  EntryHeading,
  EntryContent,
  EntryMeta,
  EntryPublication,
  EntryExtras,
  Author,
  Comments,
  Toolbar
  // PendingModerationAlert
} from '../../components';

import type { UserType, AnswerType, AuthorType } from '../../types';
import { AnswerBlock, AnswerDetail, AnswerBadge } from './partials';

type Props = {
  questionTitle: string,
  questionPath: string,
  answer: AnswerType,
  questionId: string,
  answerRef?: Function,
  isSelected?: boolean,
  isAuthor: boolean,
  isQuestionAuthor: boolean,
  blockVote: boolean,
  voted: boolean,
  handleLike: Function,
  editAnswer: Function,
  deleteAnswer: Function,
  selectAnswer: Function,
  addComment: Function,
  deleteComment: Function,
  user: UserType,
  authors: {
    [key: string]: AuthorType
  },
  author: AuthorType
};

type State = {
  isEditing: boolean,
  selectedProducts: Array<Object>
};

export class Answer extends React.Component<Props, State> {
  answerEditor: Editor;
  commentEditor: Editor;

  state = {
    isEditing: false,
    selectedProducts: this.props.answer.products || []
  };

  handleVote = () => {
    const { handleLike, answer, questionId } = this.props;
    handleLike(answer.id, questionId);
  };

  focusEditor = () => {
    this.answerEditor.focus();
  };

  handleToggleEditing = () => {
    this.setState(state => ({
      isEditing: !state.isEditing,
      selectedProducts: this.props.answer.products
    }));
  };

  handleEdition = () => {
    const { editAnswer, answer } = this.props;
    const { selectedProducts } = this.state;
    const content = this.answerEditor.props.editorState.getCurrentContent();
    const rawContent = convertToRaw(content);

    const editedAnswer = {
      rawContent,
      plainText: content.getPlainText(),
      products: selectedProducts
    };

    editAnswer(answer.id, editedAnswer);
  };

  handleDeletion = () => {
    const { deleteAnswer, answer } = this.props;
    deleteAnswer(answer.id);
  };

  handleSelection = () => {
    const { selectAnswer, answer } = this.props;
    selectAnswer(answer.id);
  };

  handleAddComment = (comment: Object) => {
    const { addComment, answer } = this.props;
    addComment(answer.id, comment);
  };

  handleDeleteComment = (commentId: number) => {
    const { deleteComment, answer } = this.props;
    deleteComment(answer.id, commentId);
  };

  addProduct = (newProduct: Object) => {
    const newProducts = [...this.state.selectedProducts];
    const pos = newProducts.findIndex((product) => {
      const id = (newProduct.entity && newProduct.entity.id) || newProduct.id;
      return product.entity.id === id;
    });

    if (pos === -1) {
      newProducts.push(newProduct);
      this.setState({ selectedProducts: newProducts });
    }
  };

  removeProduct = (products: Array<Object>) => {
    const { selectedProducts } = this.state;

    // Nothing to delete
    if (!selectedProducts.length) return;
    if (products.length === selectedProducts.length) return;

    // All previous products have been removed
    if (!products.length) {
      this.setState({ selectedProducts: [] });
    }

    // Remove deleted products
    const newProducts = selectedProducts.filter(product => products.includes(product.entity.id));
    this.setState({ selectedProducts: newProducts });
  };

  render() {
    const {
      questionTitle,
      questionPath,
      answer,
      answerRef,
      isSelected,
      isAuthor,
      isQuestionAuthor,
      author,
      user,
      authors,
      voted
    } = this.props;
    const { isEditing, selectedProducts } = this.state;
    const isUpdated = answer.updated && answer.updated.date !== answer.created;
    const hasProBadge =
      (author && ['company', 'adviser'].includes(author.type)) || Boolean(author.adviserAt && author.adviserAt.badges);
    const questionURL = `https://${process.env.FINECT_BASEURL || 'www.finect.com'}${questionPath}`;

    return (
      <AnswerBlock ref={answerRef}>
        <EntryHeading id={`respuesta-${answer.id}`} pro={hasProBadge}>
          {author ? (
            <Author
              author={author}
              action={isSelected ? 'resuelve la pregunta' : 'responde'}
              isUser={user.isLoggedIn && author.id === user.id}
              loggedIn={user.isLoggedIn}
            />
          ) : null}
          {isSelected ? (
            <AnswerBadge absolute={hasProBadge}>
              <Check size="18" stokeWidth="3" /> Mejor respuesta
            </AnswerBadge>
          ) : null}
          {hasProBadge ? (
            <AnswerBadge pro extramargin={isSelected}>
              Respuesta profesional
            </AnswerBadge>
          ) : null}
        </EntryHeading>

        <AnswerDetail pro={hasProBadge}>
          <EntryContent hasBadge={hasProBadge || isSelected}>
            {!answer.moderated ? <PendingModerationAlert type="answer" /> : null}

            <div>
              {answer.body.content.includes('blocks') ? (
                <div>
                  <EntryEditor
                    inputRef={el => (this.answerEditor = el)}
                    content={{ type: 'blocks', body: answer.body.content }}
                    focusEditor={this.focusEditor}
                    handleToggleEditing={isAuthor ? this.handleToggleEditing : null}
                    handleEdition={isAuthor ? this.handleEdition : null}
                    handleDeletion={isAuthor ? this.handleDeletion : null}
                    handleAddProduct={this.addProduct}
                    handleRemoveProduct={this.removeProduct}
                    decorators={['mentions', 'products', 'link', 'inlink', 'images']}
                    readOnly
                  />
                </div>
              ) : (
                <Paragraph>{answer.body.content}</Paragraph>
              )}
            </div>
          </EntryContent>
          <EntryMeta>
            <EntryPublication
              publishDate={answer.created}
              lastRevision={isUpdated && answer.updated ? answer.updated.date : null}
              anchor={`#respuesta-${answer.id}`}
            />
            {isEditing ?
              selectedProducts.length ? (
                <MentionedProducts products={selectedProducts} />
              ) : null
              : answer.products.length ? (
                <MentionedProducts products={answer.products} collapsed />
              ) : null}
          </EntryMeta>
          <EntryExtras>
            <Toolbar
              adviser={
                hasProBadge && {
                  id: author.id,
                  name: author.name
                }
              }
              like={{
                qty: answer.stats.votes,
                action: this.handleVote,
                done: voted,
                disable: isAuthor ? 'No puedes realizar esta acción sobre tus respuestas' : ''
              }}
              select={
                isQuestionAuthor
                  ? {
                    action: this.handleSelection,
                    done: isSelected,
                    disable: isSelected ? 'disable' : ''
                  }
                  : null
              }
              share={{
                title: `Respuesta de ${author.name || author.alias} a: ${questionTitle}`,
                path: `${questionPath}%23respuesta-${answer.id}`
              }}
            />
          </EntryExtras>
          <Comments
            comments={answer.comments}
            user={user}
            authors={authors}
            handleDeleteComment={this.handleDeleteComment}
            handleAddComment={this.handleAddComment}
            questionURL={questionURL}
          />
        </AnswerDetail>
      </AnswerBlock>
    );
  }
}
